// A/B Test utilities for Auth UI experiment
import * as Sentry from '@sentry/vue'
import { AUTH_UI_ABTEST_VARIANT } from '@/constant/storageKeys'

const VARIANTS = {
  CONTROL: 'control',
  TREATMENT: 'treatment',
}

// Treatment rate: percentage of users who will see the new UI (treatment)
// Change this single value to adjust the A/B test distribution
const TREATMENT_RATE = 0

/**
 * Assign a variant to the user based on Math.random()
 * Returns 'control' or 'treatment' based on TREATMENT_RATE
 * @returns {string} The assigned variant
 */
const assignAuthUIVariant = () => {
  const random = Math.random()
  return random < TREATMENT_RATE ? VARIANTS.TREATMENT : VARIANTS.CONTROL
}

/**
 * Get the current Auth UI variant for the user
 * If not assigned yet, assign a new variant and store in localStorage
 * @returns {string} The variant ('control' or 'treatment')
 */
const getAuthUIVariant = () => {
  try {
    // Try to get existing variant from localStorage
    const storedVariant = localStorage.getItem(AUTH_UI_ABTEST_VARIANT)

    if (storedVariant && (storedVariant === VARIANTS.CONTROL || storedVariant === VARIANTS.TREATMENT)) {
      return storedVariant
    }

    // No valid variant found, assign a new one
    const newVariant = assignAuthUIVariant()
    localStorage.setItem(AUTH_UI_ABTEST_VARIANT, newVariant)
    return newVariant
  } catch (error) {
    // localStorage might not be available (private browsing, etc.)
    // Fallback to control variant
    Sentry.captureException(error, {
      extra: {
        context: 'getAuthUIVariant',
        message: 'Failed to access localStorage for Auth UI A/B test',
      },
    })
    return VARIANTS.CONTROL
  }
}

/**
 * Clear the Auth UI variant assignment
 * Useful for testing or resetting the experiment
 */
const clearAuthUIVariant = () => {
  try {
    localStorage.removeItem(AUTH_UI_ABTEST_VARIANT)
  } catch (error) {
    // Silent fail if localStorage is not available
  }
}

export {
  getAuthUIVariant,
  assignAuthUIVariant,
  clearAuthUIVariant,
  VARIANTS,
}
